import { Link } from 'react-router-dom'
import styled from 'styled-components'

export const PageShell = styled.div`
  display: flex;
  min-height: 100dvh;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.text};
  background:
    radial-gradient(circle at 50% -220px, ${({ theme }) => theme.colors.primarySoft} 0, transparent 520px),
    ${({ theme }) => theme.colors.background};
`

export const PolicyTopbar = styled.header`
  position: sticky;
  z-index: 20;
  top: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.topBar};
  backdrop-filter: blur(14px);
`

export const PolicyTopbarInner = styled.div`
  display: flex;
  width: min(1180px, calc(100% - 48px));
  min-height: 68px;
  margin: 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 20px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    width: calc(100% - 32px);
    min-height: 62px;
    gap: 12px;
  }
`

export const BrandLink = styled(Link)`
  display: inline-flex;
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    display: block;
    width: 92px;
    height: auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    svg {
      width: 78px;
    }
  }
`

export const PolicyNavigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  font-weight: 650;

  a {
    padding: 8px 11px;
    border-radius: ${({ theme }) => theme.radius.pill};
    white-space: nowrap;
    transition: color 150ms ease, background 150ms ease;
  }

  a:hover {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  a[aria-current='page'] {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    font-size: 12px;

    a {
      padding: 7px 8px;
    }
  }
`

export const PolicyLayout = styled.main`
  width: min(${({ $narrow }) => ($narrow ? '880px' : '1180px')}, calc(100% - 48px));
  margin: 0 auto;
  padding-bottom: 96px;
  flex: 1 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    width: calc(100% - 32px);
    padding-bottom: 64px;
  }
`

export const PolicyHeader = styled.section`
  max-width: 780px;
  padding: 64px 0 42px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding: 42px 0 32px;
  }
`

export const PageEyebrow = styled.p`
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.primaryAccent};
  font-size: 0.76rem;
  font-weight: 750;
  letter-spacing: 0.1em;
`

export const StatusBadge = styled.span`
  display: inline-flex;
  min-height: 28px;
  padding: 5px 10px;
  align-items: center;
  border: 1px solid ${({ theme, $isCurrent }) => (
    $isCurrent ? theme.colors.primaryAccent : theme.colors.border
  )};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $isCurrent }) => (
    $isCurrent ? theme.colors.primary : theme.colors.textMuted
  )};
  background: ${({ theme, $isCurrent }) => (
    $isCurrent ? theme.colors.primarySoft : theme.colors.surface
  )};
  font-size: 12px;
  font-weight: 750;
`

export const PageHeader = styled.h1`
  margin: 0;
  font-size: clamp(1.8rem, 4vw, 2.35rem);
  line-height: 1.25;
  letter-spacing: -0.04em;
`

export const PageDescription = styled.p`
  max-width: 680px;
  margin: 10px 0 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.96rem;
  line-height: 1.7;
  word-break: keep-all;
`

export const PolicyHeaderFooter = styled.div`
  display: flex;
  margin-top: 28px;
  align-items: center;
  gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }
`

export const MetaList = styled.dl`
  display: flex;
  margin: 0;
  gap: 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }
`

export const MetaItem = styled.div`
  display: flex;
  gap: 8px;

  span {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
  }
`

export const SecondaryLink = styled(Link)`
  display: inline-flex;
  min-height: 42px;
  padding: 10px 16px;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 13px;
  font-weight: 700;
  transition: color 150ms ease, border-color 150ms ease, background 150ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryAccent};
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`

export const DocumentReturnLink = styled(Link)`
  display: inline-flex;
  margin-bottom: 28px;
  align-items: center;
  gap: 7px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
  font-weight: 700;
  transition: color 150ms ease;

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    margin-bottom: 22px;
  }
`

export const PolicyContent = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  align-items: start;
  gap: 28px;

  @media (max-width: ${({ theme }) => theme.breakpoint.tablet}) {
    grid-template-columns: 220px minmax(0, 1fr);
    gap: 20px;
  }

  @media (max-width: 840px) {
    grid-template-columns: minmax(0, 1fr);
  }
`

export const TableOfContents = styled.nav`
  position: sticky;
  top: 92px;
  max-height: calc(100dvh - 116px);
  padding: 22px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};

  > strong {
    display: block;
    margin-bottom: 14px;
    font-size: 15px;
  }

  ol {
    margin: 0;
    padding: 0;
    list-style: none;
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 13px;
    line-height: 1.5;
  }

  li + li {
    margin-top: 3px;
  }

  a {
    display: block;
    padding: 6px 8px;
    border-radius: 6px;
    word-break: keep-all;
  }

  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primarySoft};
  }

  @media (max-width: 840px) {
    position: static;
    max-height: none;
    padding: 20px;

    ol {
      display: grid;
      grid-template-columns: 1fr;
    }
  }
`

export const DocumentCard = styled.article`
  min-width: 0;
  padding: clamp(24px, 5vw, 52px);
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadow.low};
`

export const DocumentBody = styled.div`
  color: ${({ theme }) => theme.colors.text};
  font-size: 15px;
  line-height: 1.75;

  a {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 650;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  code {
    padding: 2px 5px;
    border-radius: 4px;
    background: ${({ theme }) => theme.colors.primarySoft};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 0.9em;
  }
`

export const DocumentHeading = styled.h2`
  margin: 64px 0 20px;
  padding-top: 8px;
  scroll-margin-top: 92px;
  font-size: clamp(22px, 3vw, 28px);
  line-height: 1.35;
  letter-spacing: -0.025em;

  &:first-child {
    margin-top: 0;
  }
`

export const DocumentSubheading = styled.h3`
  margin: 38px 0 16px;
  font-size: 19px;
  line-height: 1.4;
  letter-spacing: -0.015em;
`

export const DocumentParagraph = styled.p`
  margin: 0 0 18px;
  word-break: keep-all;
  overflow-wrap: anywhere;
`

export const PolicyList = styled.ul`
  margin: 0 0 22px;
  padding-left: 22px;

  li + li {
    margin-top: 7px;
  }
`

export const TableScroll = styled.div`
  margin: 16px 0 24px;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.small};

  &:focus-visible {
    outline-offset: 3px;
  }
`

export const PolicyTable = styled.table`
  width: 100%;
  min-width: 680px;
  border-collapse: collapse;
  font-size: 13px;
  line-height: 1.55;

  th,
  td {
    padding: 13px 14px;
    border-right: 1px solid ${({ theme }) => theme.colors.border};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    text-align: left;
    vertical-align: top;
    word-break: keep-all;
  }

  th:last-child,
  td:last-child {
    border-right: 0;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  th {
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.primarySoft};
    font-weight: 750;
  }
`

export const HistoryPageIntro = styled.section`
  padding: 64px 0 36px;

  ${PageHeader} {
    margin-top: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    padding: 42px 0 28px;
  }
`

export const HistoryList = styled.div`
  display: grid;
  gap: 14px;
`

export const HistoryCard = styled.article`
  display: flex;
  padding: 26px 28px;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surface};

  h2 {
    margin: 12px 0 8px;
    font-size: 20px;
  }

  > a {
    flex: 0 0 auto;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 14px;
    font-weight: 750;
  }

  > a:hover {
    text-decoration: underline;
  }

  @media (max-width: ${({ theme }) => theme.breakpoint.mobile}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

export const HistoryMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 18px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 13px;
`

export const ArchiveEmptyState = styled.div`
  display: flex;
  margin-top: 18px;
  padding: 20px 24px;
  flex-direction: column;
  gap: 4px;
  border-radius: ${({ theme }) => theme.radius.medium};
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) => theme.colors.primarySoft};
  font-size: 14px;

  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`

export const PolicyNotFound = styled.main`
  display: grid;
  width: min(620px, calc(100% - 32px));
  min-height: 50vh;
  margin: 0 auto;
  flex: 1 0 auto;
  place-content: center;
  justify-items: start;
  gap: 16px;

  p {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
  }
`
